import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  isSameCalendarDay,
  parseDateValue,
  toDateValue,
  toDatetimeLocalValue,
} from '../lib/datetime-local';

const MOBILE_MQ = '(max-width: 639px)';

type DatePickerMode = 'date' | 'datetime';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  mode?: DatePickerMode;
  required?: boolean;
  clearable?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
}

function getMonthDays(viewMonth: Date): (Date | null)[] {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDate = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;

  const days: (Date | null)[] = Array.from({ length: startOffset }, () => null);
  for (let day = 1; day <= lastDate; day++) {
    days.push(new Date(year, month, day));
  }
  return days;
}

function parseValue(value: string, mode: DatePickerMode) {
  if (!value) {
    return { date: null as Date | null, hours: 9, minutes: 0 };
  }

  if (mode === 'date') {
    return { date: parseDateValue(value), hours: 0, minutes: 0 };
  }

  const [datePart, timePart = '09:00'] = value.split('T');
  const [hours, minutes] = timePart.split(':').map(Number);
  return {
    date: parseDateValue(datePart),
    hours: Number.isFinite(hours) ? hours : 9,
    minutes: Number.isFinite(minutes) ? minutes : 0,
  };
}

function buildValue(date: Date, hours: number, minutes: number, mode: DatePickerMode): string {
  if (mode === 'date') return toDateValue(date);
  const base = toDatetimeLocalValue(date);
  const [datePart] = base.split('T');
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${datePart}T${pad(hours)}:${pad(minutes)}`;
}

interface CalendarPanelProps {
  viewMonth: Date;
  selectedDate: Date | null;
  onViewMonthChange: (month: Date) => void;
  onSelectDate: (date: Date) => void;
  locale: string;
}

const CalendarPanel = ({
  viewMonth,
  selectedDate,
  onViewMonthChange,
  onSelectDate,
  locale,
}: CalendarPanelProps) => {
  const { t } = useTranslation();
  const today = new Date();
  const days = getMonthDays(viewMonth);
  const weekdayLabels = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(2024, 0, 1 + i);
    return day.toLocaleDateString(locale, { weekday: 'short' });
  });

  const goToPrevMonth = () => {
    const prev = new Date(viewMonth);
    prev.setMonth(prev.getMonth() - 1);
    onViewMonthChange(prev);
  };

  const goToNextMonth = () => {
    const next = new Date(viewMonth);
    next.setMonth(next.getMonth() + 1);
    onViewMonthChange(next);
  };

  return (
    <div className="date-picker-panel">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={goToPrevMonth}
          className="btn-ghost min-h-10 min-w-10"
          aria-label={t('Предыдущий месяц')}
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-bold text-slate-900 capitalize">
          {viewMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          onClick={goToNextMonth}
          className="btn-ghost min-h-10 min-w-10"
          aria-label={t('Следующий месяц')}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdayLabels.map((label) => (
          <div
            key={label}
            className="section-label text-center py-1"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const isToday = isSameCalendarDay(day, today);
          const isSelected = selectedDate ? isSameCalendarDay(day, selectedDate) : false;

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelectDate(day)}
              className={`aspect-square min-h-10 rounded-xl text-sm font-bold transition-colors ${
                isSelected
                  ? 'bg-primary text-white shadow-sm'
                  : isToday
                    ? 'bg-primary-soft text-primary'
                    : 'text-slate-900 hover:bg-slate-100'
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DatePicker = ({
  value,
  onChange,
  mode = 'date',
  required,
  clearable,
  className = '',
  id,
  placeholder,
}: DatePickerProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const autoId = useId();
  const inputId = id ?? autoId;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_MQ).matches);

  const parsed = parseValue(value, mode);
  const [viewMonth, setViewMonth] = useState(() => parsed.date ?? new Date());
  const [draftDate, setDraftDate] = useState<Date | null>(parsed.date);
  const [draftHours, setDraftHours] = useState(parsed.hours);
  const [draftMinutes, setDraftMinutes] = useState(parsed.minutes);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChangeMq = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChangeMq);
    return () => mq.removeEventListener('change', onChangeMq);
  }, []);

  const openPicker = () => {
    const parsedOpen = parseValue(value, mode);
    setViewMonth(parsedOpen.date ?? new Date());
    setDraftDate(parsedOpen.date);
    setDraftHours(parsedOpen.hours);
    setDraftMinutes(parsedOpen.minutes);
    setOpen(true);
  };

  const displayValue = (() => {
    if (!value) return '';
    const { date, hours, minutes } = parseValue(value, mode);
    if (!date) return '';
    if (mode === 'date') {
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }
    const datetime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
    );
    return datetime.toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  })();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open || isMobile) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      close();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, isMobile, close]);

  useLayoutEffect(() => {
    if (!open || isMobile || !triggerRef.current || !panelRef.current) return;

    const panel = panelRef.current;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 320;
    const panelHeight = mode === 'datetime' ? 420 : 360;
    const gap = 8;

    let left = rect.left;
    if (left + panelWidth > window.innerWidth - 16) {
      left = window.innerWidth - panelWidth - 16;
    }
    left = Math.max(16, left);

    let top = rect.bottom + gap;
    if (top + panelHeight > window.innerHeight - 16) {
      top = rect.top - panelHeight - gap;
    }
    top = Math.max(16, top);

    panel.style.position = 'fixed';
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.style.width = `${panelWidth}px`;
    panel.style.zIndex = '200';
  }, [open, isMobile, mode]);

  const applyDate = (date: Date) => {
    if (mode === 'date') {
      onChange(buildValue(date, 0, 0, mode));
      close();
      return;
    }
    setDraftDate(date);
  };

  const applyDateTime = () => {
    const targetDate = draftDate ?? new Date();
    onChange(buildValue(targetDate, draftHours, draftMinutes, mode));
    close();
  };

  const timeControls = mode === 'datetime' && (
    <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-slate-100">
      <div className="space-y-1">
        <label className="section-label">
          {t('Часы')}
        </label>
        <select
          className="input-field py-2.5 text-sm"
          value={draftHours}
          onChange={(e) => setDraftHours(Number(e.target.value))}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="section-label">
          {t('Минуты')}
        </label>
        <select
          className="input-field py-2.5 text-sm"
          value={draftMinutes}
          onChange={(e) => setDraftMinutes(Number(e.target.value))}
        >
          {Array.from({ length: 60 }, (_, m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const panelContent = (
    <>
      <CalendarPanel
        viewMonth={viewMonth}
        selectedDate={mode === 'datetime' ? draftDate : parsed.date}
        onViewMonthChange={setViewMonth}
        onSelectDate={applyDate}
        locale={locale}
      />
      {timeControls}
      {mode === 'datetime' && (
        <div className="flex gap-2 pt-3 mt-1">
          <button type="button" className="btn-secondary flex-1 text-sm py-2.5" onClick={close}>
            {t('Отмена')}
          </button>
          <button type="button" className="btn-primary flex-1 text-sm py-2.5" onClick={applyDateTime}>
            {t('Готово')}
          </button>
        </div>
      )}
    </>
  );

  const popover =
    open &&
    createPortal(
      isMobile ? (
        <div
          className="fixed inset-0 z-[200] flex items-end bg-slate-900/40 backdrop-blur-sm"
          onClick={close}
          role="presentation"
        >
          <div
            ref={panelRef}
            className="w-full bg-white rounded-t-[1.75rem] border border-app-border shadow-card p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('Выбор даты')}
          >
            <div className="flex justify-center mb-3 sm:hidden">
              <span className="block w-11 h-1.5 rounded-full bg-slate-300/90" />
            </div>
            {panelContent}
          </div>
        </div>
      ) : (
        <div
          ref={panelRef}
          className="bg-white rounded-3xl border border-app-border shadow-card p-4"
          style={{ position: 'fixed', zIndex: 200, width: 320 }}
          role="dialog"
          aria-modal="true"
          aria-label={t('Выбор даты')}
        >
          {panelContent}
        </div>
      ),
      document.body,
    );

  return (
    <div className="relative">
      {required && (
        <input
          tabIndex={-1}
          className="sr-only"
          value={value}
          required
          onChange={() => {}}
          aria-hidden
        />
      )}
      <div className="flex items-center gap-1">
        <button
          ref={triggerRef}
          id={inputId}
          type="button"
          onClick={openPicker}
          className={`input-field font-medium text-left flex items-center justify-between gap-2 flex-1 min-w-0 ${
            !value ? 'text-slate-400' : ''
          } ${className}`.trim()}
        >
          <span className="truncate">{displayValue || placeholder || t('Выберите дату')}</span>
          <CalendarIcon size={16} className="text-slate-400 shrink-0" />
        </button>
        {clearable && value && (
          <button
            type="button"
            className="btn-ghost shrink-0 min-h-11 min-w-11"
            onClick={() => onChange('')}
            aria-label={t('Очистить')}
          >
            <X size={16} />
          </button>
        )}
      </div>
      {popover}
    </div>
  );
};

export default DatePicker;
